/**
 * Shared laboratory display primitives.
 *
 * Kept in one file rather than one component per file because each is a few
 * lines and they are always used together — a result row shows a flag, a
 * status and sometimes a barcode. The IP Case Sheet's own `ui.tsx` set the
 * same precedent.
 */
import { useEffect, useRef, type ReactNode } from 'react';
import { Badge } from '@/components/ui/Badge';
import { cn } from '@/lib/cn';
import type { LabFlag, LabPriority, LabStatus, ProcessingType, RejectionReason } from './types';

// ── Flags ────────────────────────────────────────────────────────

/**
 * The abnormal-result flag.
 *
 * NOT_EVALUATED renders as a muted "—", never as normal: the server uses it
 * for "no range applied, or this value isn't comparable", and showing that as
 * a clean result is the one presentation mistake that could actually harm a
 * patient. NORMAL renders as nothing at all, so the eye finds the abnormal
 * rows — a column of green ticks hides the one row that matters.
 */
const FLAG_META: Record<LabFlag, { label: string; variant: 'neutral' | 'warn' | 'danger' | 'info'; short: string }> = {
  NORMAL:        { label: 'Normal',        variant: 'neutral', short: '' },
  LOW:           { label: 'Low',           variant: 'warn',    short: 'L' },
  HIGH:          { label: 'High',          variant: 'warn',    short: 'H' },
  CRITICAL_LOW:  { label: 'Critical low',  variant: 'danger',  short: 'LL' },
  CRITICAL_HIGH: { label: 'Critical high', variant: 'danger',  short: 'HH' },
  ABNORMAL:      { label: 'Abnormal',      variant: 'warn',    short: 'A' },
  NOT_EVALUATED: { label: 'Not evaluated', variant: 'neutral', short: '—' },
};

export function AbnormalFlag({ flag, compact = false }: { flag: LabFlag | null; compact?: boolean }) {
  if (!flag || flag === 'NORMAL') return null;
  const meta = FLAG_META[flag];
  if (!meta) return null;
  if (flag === 'NOT_EVALUATED') {
    return <span className="text-[11px] text-muted-foreground" title="No reference range applied — this value has not been judged.">not evaluated</span>;
  }
  return <Badge variant={meta.variant}>{compact ? meta.short : meta.label}</Badge>;
}

export const isCriticalFlag = (f: LabFlag | null | undefined) => f === 'CRITICAL_LOW' || f === 'CRITICAL_HIGH';
export const isAbnormalFlag = (f: LabFlag | null | undefined) =>
  !!f && f !== 'NORMAL' && f !== 'NOT_EVALUATED';

// ── Statuses ─────────────────────────────────────────────────────

const STATUS_META: Record<LabStatus, { label: string; variant: 'neutral' | 'warn' | 'info' | 'success' | 'danger' | 'caution' }> = {
  ORDERED:               { label: 'Ordered',              variant: 'neutral' },
  PENDING_COLLECTION:    { label: 'Pending collection',   variant: 'warn' },
  COLLECTED:             { label: 'Collected',            variant: 'info' },
  RECEIVED:              { label: 'Received',             variant: 'info' },
  ACCEPTED:             { label: 'Accepted',             variant: 'info' },
  PROCESSING:            { label: 'Processing',           variant: 'info' },
  SENT_TO_REFERENCE_LAB: { label: 'Sent to reference lab', variant: 'info' },
  AWAITING_REPORT:       { label: 'Awaiting report',      variant: 'warn' },
  REPORT_RECEIVED:       { label: 'Report received',      variant: 'info' },
  REPORT_UPLOADED:       { label: 'Report uploaded',      variant: 'caution' },
  RESULT_ENTERED:        { label: 'Awaiting verification', variant: 'caution' },
  VERIFIED:              { label: 'Verified',             variant: 'success' },
  REPORTED:              { label: 'Reported',             variant: 'success' },
  REJECTED:              { label: 'Rejected',             variant: 'danger' },
  RECOLLECTION_REQUIRED: { label: 'Recollection needed',  variant: 'danger' },
  CANCELLED:             { label: 'Cancelled',            variant: 'neutral' },
};

/**
 * The outside path reuses RESULT_ENTERED and REPORTED (see
 * migration_lab_outsourcing.sql) but names them the way that desk does:
 * "pending verification" and "finalized".
 */
const OUTSOURCED_LABELS: Partial<Record<LabStatus, string>> = {
  PENDING_COLLECTION: 'Ordered',
  COLLECTED: 'Sample collected',
  RESULT_ENTERED: 'Pending verification',
  REPORTED: 'Finalized',
};

export function labStatusLabel(status: LabStatus, processingType?: ProcessingType | null): string {
  if (processingType === 'outsourced' && OUTSOURCED_LABELS[status]) return OUTSOURCED_LABELS[status] as string;
  return STATUS_META[status]?.label ?? status;
}

export function LabStatusBadge({ status, processingType }: { status: LabStatus | null | undefined; processingType?: ProcessingType | null }) {
  if (!status) return null;
  const meta = STATUS_META[status];
  return <Badge variant={meta?.variant ?? 'neutral'}>{labStatusLabel(status, processingType)}</Badge>;
}

/** "Outside · Metropolis" — shown wherever a test's routing matters. Nothing for in-house tests. */
export function OutsideLabBadge({ processingType, referenceLabName }: { processingType?: ProcessingType | null; referenceLabName?: string | null }) {
  if (processingType !== 'outsourced') return null;
  return (
    <Badge variant="info" className="ml-1" title="Processed by an outside / reference laboratory">
      Outside{referenceLabName ? ` · ${referenceLabName}` : ''}
    </Badge>
  );
}

export function PriorityBadge({ priority }: { priority: LabPriority | null | undefined }) {
  // Routine is the overwhelming majority and gets no badge — the point of the
  // badge is that urgent/STAT stand out in a list of a hundred.
  if (!priority || priority === 'routine') return null;
  return (
    <Badge variant={priority === 'stat' ? 'danger' : 'warn'}>
      {priority === 'stat' ? 'STAT' : 'Urgent'}
    </Badge>
  );
}

export const REJECTION_REASONS: Array<{ value: RejectionReason; label: string }> = [
  { value: 'insufficient',      label: 'Insufficient sample' },
  { value: 'hemolysed',         label: 'Hemolysed' },
  { value: 'clotted',           label: 'Clotted' },
  { value: 'wrong_container',   label: 'Wrong container' },
  { value: 'wrong_sample_type', label: 'Wrong sample type' },
  { value: 'leaking',           label: 'Leaking' },
  { value: 'expired',           label: 'Expired' },
  { value: 'improper_storage',  label: 'Improper storage' },
  { value: 'patient_id_error',  label: 'Patient identification error' },
  { value: 'other',             label: 'Other (state remarks)' },
];

export const rejectionLabel = (r: RejectionReason | null | undefined) =>
  REJECTION_REASONS.find((x) => x.value === r)?.label ?? r ?? '—';

// ── Formatting ───────────────────────────────────────────────────

export function fmtDateTime(v: string | null | undefined): string {
  if (!v) return '—';
  return new Date(v).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
}

export function fmtDate(v: string | null | undefined): string {
  if (!v) return '—';
  return new Date(v).toLocaleDateString('en-IN', { dateStyle: 'medium' });
}

/** "3h 20m ago" / "in 40m" — a work queue reads elapsed time, not a clock. */
export function fmtRelative(v: string | null | undefined): string {
  if (!v) return '—';
  const diff = Date.now() - new Date(v).getTime();
  const abs = Math.abs(diff);
  const mins = Math.round(abs / 60000);
  const text = mins < 60
    ? `${mins}m`
    : mins < 1440
      ? `${Math.floor(mins / 60)}h ${mins % 60}m`
      : `${Math.floor(mins / 1440)}d`;
  return diff >= 0 ? `${text} ago` : `in ${text}`;
}

// ── Barcode ──────────────────────────────────────────────────────

/**
 * Code 128 (subset B/C), drawn to a canvas.
 *
 * Drawn in the browser rather than fetched as a server-rendered image on
 * purpose: a label printer is typically 203 dpi, and an upscaled server
 * bitmap at that size is the difference between a barcode that scans first
 * time and one a technician re-keys by hand. The canvas prints at the
 * printer's own resolution.
 *
 * Implemented here rather than pulled from a package because the encoder is
 * ~60 lines and adding a dependency to the frontend for it would be a bigger
 * commitment than the code — the barcodes this produces are all-digit, which
 * is the simplest case in the spec (see utils/labNumbering.js for why the
 * format is `S` + digits).
 */
const CODE128_PATTERNS = [
  '11011001100', '11001101100', '11001100110', '10010011000', '10010001100', '10001001100',
  '10011001000', '10011000100', '10001100100', '11001001000', '11001000100', '11000100100',
  '10110011100', '10011011100', '10011001110', '10111001100', '10011101100', '10011100110',
  '11001110010', '11001011100', '11001001110', '11011100100', '11001110100', '11101101110',
  '11101001100', '11100101100', '11100100110', '11101100100', '11100110100', '11100110010',
  '11011011000', '11011000110', '11000110110', '10100011000', '10001011000', '10001000110',
  '10110001000', '10001101000', '10001100010', '11010001000', '11000101000', '11000100010',
  '10110111000', '10110001110', '10001101110', '10111011000', '10111000110', '10001110110',
  '11101110110', '11010001110', '11000101110', '11011101000', '11011100010', '11011101110',
  '11101011000', '11101000110', '11100010110', '11101101000', '11101100010', '11100011010',
  '11101111010', '11001000010', '11110001010', '10100110000', '10100001100', '10010110000',
  '10010000110', '10000101100', '10000100110', '10110010000', '10110000100', '10011010000',
  '10011000010', '10000110100', '10000110010', '11000010010', '11001010000', '11110111010',
  '11000010100', '10001111010', '10100111100', '10010111100', '10010011110', '10111100100',
  '10011110100', '10011110010', '11110100100', '11110010100', '11110010010', '11011011110',
  '11011110110', '11110110110', '10101111000', '10100011110', '10001011110', '10111101000',
  '10111100010', '11110101000', '11110100010', '10111011110', '10111101110', '11101011110',
  '11110101110', '11010000100', '11010010000', '11010011100', '11000111010',
];
const START_B = 104;
const STOP = '1100011101011';

function encodeCode128B(text: string): string {
  let checksum = START_B;
  let bars = CODE128_PATTERNS[START_B] as string;
  text.split('').forEach((ch, i) => {
    const value = ch.charCodeAt(0) - 32;
    bars += CODE128_PATTERNS[value] ?? '';
    checksum += value * (i + 1);
  });
  bars += CODE128_PATTERNS[checksum % 103] ?? '';
  return bars + STOP;
}

export function Barcode({ value, height = 48, moduleWidth = 2, className }: {
  value: string; height?: number; moduleWidth?: number; className?: string;
}) {
  const ref = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas || !value) return;
    const bars = encodeCode128B(value);
    const quiet = 10 * moduleWidth;               // the mandatory quiet zone; without it many scanners simply refuse
    canvas.width = bars.length * moduleWidth + quiet * 2;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#000000';
    for (let i = 0; i < bars.length; i++) {
      if (bars[i] === '1') ctx.fillRect(quiet + i * moduleWidth, 0, moduleWidth, height);
    }
  }, [value, height, moduleWidth]);

  if (!value) return null;
  return (
    <div className={cn('inline-flex flex-col items-center', className)}>
      {/* Bars are always black on white regardless of theme — a dark-mode
          barcode is unscannable, and the label goes to a printer anyway. */}
      <canvas ref={ref} className="bg-white" aria-label={`Barcode ${value}`} />
      <span className="mt-1 font-mono text-[11px] tracking-widest text-black">{value}</span>
    </div>
  );
}

// ── Layout helpers ───────────────────────────────────────────────

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-[13px]">{children ?? '—'}</div>
    </div>
  );
}

export function SectionTitle({ children, right }: { children: ReactNode; right?: ReactNode }) {
  return (
    <div className="mb-2 flex items-center justify-between">
      <h3 className="text-sm font-semibold">{children}</h3>
      {right}
    </div>
  );
}
